<template>
  <el-dialog
    v-model="visible"
    width="500px"
    @close="onClose"
    append-to-body
    destroy-on-close>
    <div class="appointment-header-visual">
      <div class="header-row">
        <div class="header-item">
          <el-icon>
            <Calendar />
          </el-icon>
          <span>{{ formatDate(appointment.date) }}</span>
          <span style="color: #888;">
            ({{ formattedDate(appointment.date) }}
            <span
              class="date-weekday"
              :class="{ weekend: isWeekend }"
            >
            {{ weekdayLabel }}
          </span>)
          </span>
        </div>
        <div class="header-item">
          <el-icon>
            <Clock />
          </el-icon>
          <span>{{ appointment.start_time }} – {{ appointment.end_time }}</span>
        </div>
      </div>
      <div class="header-row">
        <div class="header-item">
          <el-icon>
            <UserFilled />
          </el-icon>
          <span>{{ appointment.user_name }}</span>
        </div>
        <div class="header-item">
          <el-icon>
            <OfficeBuilding />
          </el-icon>
          <span>{{ appointment.room_name }}</span>
        </div>
      </div>
      <div class="header-row status-row">
        <div class="header-item">
          <el-icon>
            <AlarmClock />
          </el-icon>
          <span>{{ appointment.duration_time || totalDuration }} мин</span>
        </div>
      </div>
    </div>

    <div v-if="!clientIsDeleted" class="client-info-card">
      <div class="client-phone" v-if="appointment.client_phone">
        <el-icon>
          <Phone />
        </el-icon>
        <a :href="`tel:${appointment.client_phone}`" class="phone-link">{{ appointment.client_phone }}</a>
        <el-tooltip content="Скопировать" placement="top">
          <el-button :icon="CopyDocument" text @click="copyPhone" class="copy-button" circle />
        </el-tooltip>
      </div>
      <div class="client-name" @click="showClientProfile" title="Показать профиль клиента">
        <el-icon>
          <UserFilled />
        </el-icon>
        <span class="name-text">{{ appointment.client_name }}</span>
        <el-button :icon="Edit" text circle @click.stop="showClientProfile" class="edit-button"
                   title="Редактировать клиента" />
      </div>
    </div>


    <el-form :model="form" label-width="110px" label-position="top">

      <!-- Услуги (мультивыбор) -->
      <el-form-item>
        <ServiceMultiSelect
          v-model="form.services"
          :services="services"
          :remote="true"
          :city-id="appointment?.city_id"
        />
      </el-form-item>

      <!-- Длительность -->
      <el-form-item label="Длительность">
        <el-input-number
          v-model="form.duration"
          :min="0"
          :step="15"
        />
        <span style="margin-left: 6px;">мин.</span>
      </el-form-item>


      <!-- Комментарий -->
      <el-form-item label="Комментарий">
        <el-input
          type="textarea"
          v-model="form.comment"
          placeholder="Комментарий"
        />
      </el-form-item>
    </el-form>

    <template #footer>
      <el-button @click="onClose">Отмена</el-button>
      <el-button type="primary" @click="save" :disabled="!canSave">Обновить</el-button>
    </template>

  </el-dialog>
</template>
<script>
import {
  AlarmClock,
  Clock,
  OfficeBuilding,
  UserFilled,
  Calendar,
  CopyDocument,
  Edit,
  Phone
} from '@element-plus/icons-vue'
import ServiceMultiSelect from '@/components/util/calendar/ServiceMultiSelect.vue'
import { mapState } from 'vuex'
import store from '@/components/store/store'

export default {
  name: 'AppointmentEditModal',
  components: { ServiceMultiSelect, Phone, AlarmClock, OfficeBuilding, UserFilled, Clock, Calendar },
  props: {
    appointment: Object,
    date: { type: String, required: true }
  },
  data() {
    return {
      visible: true,
      form: {},
      edit: {
        name: this.appointment.client_name,
        phone: this.appointment.client_phone
      },
      initialized: false
    }
  },
  watch: {
    appointment: {
      handler(val) {
        this.initialized = false
        this.form = JSON.parse(JSON.stringify(val || {}))

        this.$nextTick(() => {
          if (this.form.duration_time) {
            this.form.duration = this.form.duration_time
          }
            // НЕ нужно else if (this.form.duration) — duration уже скопирован выше!
          // иначе считаем сумму по услугам
          else if (!this.form.duration) {
            this.form.duration = this.totalDuration
          }
          this.initialized = true
        })
      },
      immediate: true
    },
    'form.services': {
      handler() {
        // Пересчёт только после инициализации!
        if (this.initialized) {
          this.form.duration = this.totalDuration;
        }
      },
      immediate: true,
      deep: true
    }
  },
  computed: {
    ...mapState('services', ['services']),
    Edit() {
      return Edit
    },
    canSave() {
      return Array.isArray(this.form.services) && this.form.services.length > 0
        && (this.form.client_id || this.form.client_name)
    },
    CopyDocument() {
      return CopyDocument
    },
    clientIsDeleted() {
      // Если нет имени и нет телефона — считаем "удалён"
      const name = (this.appointment.client_name || '').trim()
      const phone = (this.appointment.client_phone || '').trim()
      // id может быть, но если имя и телефон пусты — это "удалён"
      return !name && !phone
    },
    weekdayLabel() {
      if (!this.date) return ''
      const d = new Date(this.date)
      if (isNaN(d.getTime())) return ''
      const weekdays = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб']
      return weekdays[d.getDay()]
    },
    isWeekend() {
      if (!this.date) return false
      const d = new Date(this.date)
      if (isNaN(d.getTime())) return false
      return d.getDay() === 0 || d.getDay() === 6
    },
    totalDuration() {
      if (this.form && this.form.services && this.form.services.length) {
        return this.form.services.reduce((sum, s) => sum + (s.duration || 0), 0)
      }
      return 0
    }
  },
  methods: {
    showClientProfile() {
      if (this.appointment.client_id) {
        this.$emit('show-client', this.appointment.client_id)
      }
    },
    copyPhone() {
      if (this.appointment.client_phone) {
        navigator.clipboard.writeText(this.appointment.client_phone).then(() => {
          this.$message.success('Скопировано')
        }).catch(() => {
          alert('Не удалось скопировать')
        })
      }
    },
    onClose(){
      this.$emit('close');
    },
    formattedDate(date) {
      const months = [
        'Января', 'Февраля', 'Марта', 'Апреля', 'Мая', 'Июня',
        'Июля', 'Августа', 'Сентября', 'Октября', 'Ноября', 'Декабря'
      ]
      // YYYY-MM-DD
      const [, month, day] = date.split('-')
      const dayStr = day.length === 1 ? `0${day}` : day
      const monthName = months[parseInt(month, 10) - 1]
      return `${dayStr} ${monthName}`
    },
    formatDate(date) {
      if (!date) return ''
      // формат "2025-06-06" → "06.06.2025"
      const parts = date.split('-')
      if (parts.length !== 3) return date
      return `${parts[2]}.${parts[1]}.${parts[0]}`
    },
    save() {
      store.dispatch('appointments/updateAppointment', this.form);
      this.$emit('close')
    }
  }
}
</script>
<style scoped>
/* можно копипастить стиль из view */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(60, 60, 90, 0.13);
  z-index: 3000;
}

.modal-window {
  background: #fff;
  padding: 30px 34px;
  border-radius: 10px;
  min-width: 350px;
  min-height: 180px;
  max-width: 95vw;
  max-height: 95vh;
  box-shadow: 0 5px 32px 0 rgba(10, 40, 60, 0.13);
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}

.appointment-header-visual {
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.header-row {
  display: flex;
  gap: 32px;
  align-items: center;
  justify-content: space-between;
}

.header-item {
  display: flex;
  align-items: center;
  font-size: 16px;
  color: #454545;
  gap: 8px;
}

.status-row {
  margin-top: 4px;
}


.client-info-card {
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 12px;
  background-color: #f9f9f9;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.client-phone {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  color: #606266;
}

.phone-link {
  color: #409EFF;
  text-decoration: none;
}

.phone-link:hover {
  text-decoration: underline;
}

.copy-button {
  padding: 0;
  margin-left: 4px;
  color: #909399;
}

.client-name {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  cursor: pointer;
  user-select: none;
}

.name-text {
  user-select: text;
}
</style>
